import outputCatalog from "./mobbiOutputSettings.json";
import mobbiVideoCatalog from "./mobbiVideoModels.json";
import mobbiImageCatalog from "./mobbiImageModels.json";
import mobbiExtraCatalog from "./mobbiExtraModels.json";
import mobbiEffectCatalog from "./mobbiVideoEffects.json";
import { creativeEffects } from "./creativeEffects";
import creativeVoiceCatalog from "./creativeVoices.json";
import { adFinishTool } from "./creativeAdFinish";

/**
 * Creative tools are sold as quality tiers (Fast / Standard / Best), not as
 * a model catalog. Model ids are internal routing detail: the tier layer maps
 * to whichever engine we currently run, so swapping an engine is a config
 * change and the page never names a third-party model (Sep 21 2026: the
 * "Advanced: choose a specific engine" picker was removed for that reason).
 */
export type MobbiImageModel = {
  company: string;
  perSecond?: boolean;
  /** Billed per started minute of the uploaded clip (dubbing). */
  perMinute?: boolean;
  /** Billed per started 1,000 characters of the prompt (speech). */
  perKChars?: boolean;
  clipLimits?: { min: number; max: number; maxMB: number };
  name: string;
  credits: number;
  type: string;
  model: string;
  multiple: boolean;
  defaults: Record<string, unknown>;
  shapeField?: string;
  shapes: Record<string, string>;
  /** What the provider returns; defaults from the tool category. */
  output?: "image" | "video" | "audio" | "model";
  /** Video-to-video models that also take a source image (motion transfer). */
  needsImage?: boolean;
  /** Image-to-video models whose second image is the end frame. */
  endFrame?: boolean;
  /** Models that take reference images instead of a single source. */
  references?: boolean;
  /** A video effect preset (Mobbi's old catalog, or a PixVerse template). */
  effect?: boolean;
  duration?: number;
  aspectRatio?: string | null;
  category?: string;
  cover?: string | null;
  engine?: string;
  voiceProvider?: "elevenlabs" | "minimax";
};
export const mobbiImageModels: Record<string, MobbiImageModel> =
  mobbiImageCatalog;
export const mobbiVideoModels: Record<string, MobbiImageModel> =
  mobbiVideoCatalog;
export const mobbiExtraModels: Record<string, MobbiImageModel> =
  mobbiExtraCatalog as Record<string, MobbiImageModel>;
/**
 * Mobbi's old effect presets (Kling, Grok, Seedance through Renderful). No
 * tool offers them since Oct 9 2026; they stay so jobs started before the
 * switch to PixVerse effects still poll and keep their names.
 */
export const mobbiEffectModels: Record<string, MobbiImageModel> =
  mobbiEffectCatalog as Record<string, MobbiImageModel>;
/** Video Effects' engines: PixVerse effect templates (shared/creativeEffects). */
export const PIXVERSE_EFFECT_ENGINE = "pixverse-effect";
export const pixverseEffectModels: Record<string, MobbiImageModel> =
  Object.fromEntries(
    creativeEffects.map((effect) => [
      effect.id,
      {
        company: "PixVerse",
        name: effect.name,
        credits: effect.credits,
        type: "image-to-video",
        model: effect.templateId,
        multiple: false,
        defaults: { resolution: "720p" },
        shapes: {},
        effect: true,
        duration: effect.seconds,
        category: effect.family,
        cover: effect.poster,
        engine: PIXVERSE_EFFECT_ENGINE,
      } satisfies MobbiImageModel,
    ]),
  );
export const creativeGatewayModels: Record<string, MobbiImageModel> = {
  ...mobbiImageModels,
  ...mobbiVideoModels,
  ...mobbiExtraModels,
  ...mobbiEffectModels,
  ...pixverseEffectModels,
};
/** PixVerse effect templates run only on Solmi's own PixVerse account. */
export const isPixverseEffectModel = (model: string) =>
  creativeGatewayModels[model]?.engine === PIXVERSE_EFFECT_ENGINE;
export type CreativeVoice = {
  voiceId: string;
  name: string;
  provider: "elevenlabs" | "minimax";
  gender: string;
  language: string;
  style: string;
};
export const creativeVoices: CreativeVoice[] =
  creativeVoiceCatalog as CreativeVoice[];
/**
 * Catalog engines whose provider is gone. OpenAI shut down the Sora API on
 * Sep 24 2026; no creative job ever ran a Sora model (prod read, Oct 11
 * 2026), so the engines leave the server allowlists and stay in the catalog
 * snapshot only.
 */
const retiredVideoEngine = (id: string) => /sora/i.test(id);
export const mobbiTextVideoModels = Object.keys(mobbiVideoModels).filter(
  (id) =>
    mobbiVideoModels[id].type === "text-to-video" && !retiredVideoEngine(id),
);
export const mobbiImageVideoModels = Object.keys(mobbiVideoModels).filter(
  (id) =>
    mobbiVideoModels[id].type === "image-to-video" && !retiredVideoEngine(id),
);
export const mobbiVideoEditModels = [
  "rf-edit-wan-2.7-videoedit",
  "rf-edit-happyhorse-1.0-video-edit",
  "lucy-edit",
  "rf-edit-gen4-aleph",
  "rf-edit-grok-imagine-video-edit",
];
const extraIds = (prefix: string) =>
  Object.keys(mobbiExtraModels).filter((id) => id.startsWith(prefix));
export const mobbiVideoExtendModels = extraIds("rf-ext-");
export const mobbiMimicModels = extraIds("rf-mimic-");
export const mobbiTransitionModels = extraIds("rf-tr-");
export const mobbiReferenceVideoModels = extraIds("rf-r2v-");
export const mobbiSpeechModels = extraIds("rf-tts-");
export const mobbiDubbingModels = extraIds("rf-dub-");
export const creativeClipLimits = (model: string) =>
  creativeGatewayModels[model]?.clipLimits || { min: 1, max: 15, maxMB: 20 };
const mobbiGenerationModels = Object.keys(mobbiImageModels).filter(
  (id) => mobbiImageModels[id].type === "text-to-image",
);
const mobbiEditModels = Object.keys(mobbiImageModels).filter(
  (id) => mobbiImageModels[id].type === "image-to-image",
);
/** The subset Mobbi's Image Editor offers for AI edits. */
const mobbiImageEditorModels = [
  "rf-edit-flux-2",
  "rf-edit-flux-2-flex",
  "rf-edit-flux-kontext-pro",
  "rf-edit-flux-kontext-max",
  "rf-edit-seedream-4",
  "rf-edit-nano-banana-pro",
].filter((id) => mobbiImageModels[id]);
/** Prompted photo presets use the strongest instruction-following editors. */
const photoPresetModels = [
  "rf-edit-nano-banana-pro",
  "rf-edit-flux-kontext-pro",
  "rf-edit-gpt-image-2",
  "seedream",
].filter((id) => id === "seedream" || mobbiImageModels[id]);
export function creativeAspectRatios(
  model: string,
  category: CreativeCategory,
) {
  const spec = creativeGatewayModels[model];
  return spec
    ? Object.keys(spec.shapes)
    : category === "image"
      ? ["1:1", "16:9", "9:16"]
      : category === "video"
        ? ["16:9", "9:16"]
        : [];
}

/**
 * Video engines we call directly (BytePlus ModelArk) instead of through the
 * Renderful gateway, billed per second of the duration the user picks.
 *
 * Sep 21 2026 — Seedance 2.0 Fast. Through the gateway it cost us
 * $0.825–1.65 a clip and was priced at 189–297 credits for a fixed 8 s.
 * Direct it is token-metered: a real 4 s 480p job used 40,594 tokens at
 * $5.60 / M (list) ≈ $0.057/s, so 720p ≈ $0.126/s. At ~$0.0175 a credit and
 * the ~2x markup the other tiers carry that was 15 credits per second.
 * Lowered to 12 the same day (Luv): our bill shows BytePlus discounts us —
 * a 10% account discount on everything, and the limited-time Seedance promo
 * (Mini is billed at exactly 60% off, so the "enterprise" promo applies to
 * us; Fast's is 25% until Oct 7 2026). Expected cost ≈ $0.095/s until then,
 * ≈ $0.113/s after, so 12 credits (≈ $0.21) is ~2.2x now and ~1.9x later.
 * BytePlus refuses input images with real human faces; those jobs are
 * resubmitted on `fallbackEndpoint` (WaveSpeed hosts the same model and
 * accepts them) — see creativeToolJobs.
 */
export type CreativeDirectVideoModel = {
  /** BytePlus ModelArk (Seedance) or Google Vertex AI (Veo, creativeVertexVeo.ts). */
  provider: "byteplus" | "vertex";
  /** The provider's model id. */
  providerModel: string;
  takesImage: boolean;
  /** The resolution a job renders at when the user picks none. */
  resolution: "480p" | "720p" | "1080p";
  durations: number[];
  defaultDuration: number;
  /** Credits per second at `resolution`. */
  creditsPerSecond: number;
  /**
   * Resolutions the user can pick, each with its own per-second rate. Absent:
   * the engine renders at `resolution` only and shows no resolution picker.
   */
  creditsPerSecondByResolution?: Record<string, number>;
  /** WaveSpeed endpoint that runs the same engine when BytePlus refuses the image. */
  fallbackEndpoint?: string;
};
export const creativeDirectVideoModels: Record<string, CreativeDirectVideoModel> = {
  "seedance-fast-t2v": {
    provider: "byteplus",
    providerModel: "dreamina-seedance-2-0-fast-260128",
    takesImage: false,
    resolution: "720p",
    durations: [5, 8, 10, 15],
    defaultDuration: 5,
    creditsPerSecond: 12,
    fallbackEndpoint: "bytedance/seedance-2.0-fast/text-to-video",
  },
  "seedance-fast-i2v": {
    provider: "byteplus",
    providerModel: "dreamina-seedance-2-0-fast-260128",
    takesImage: true,
    resolution: "720p",
    durations: [5, 8, 10, 15],
    defaultDuration: 5,
    creditsPerSecond: 12,
    fallbackEndpoint: "bytedance/seedance-2.0-fast/image-to-video",
  },
  /**
   * Oct 11 2026 — Veo 3.1 Fast text-to-video on Google Vertex AI, the same
   * service account and model the Director's Veo arms run in production
   * (scripts/director-prototype/tools/vertexVeo.ts), called directly. The
   * Premium tier of the AI Video Generator since its live call succeeded
   * (scripts/creative-veo/smoke.mts, see videoTiers). Clips are 4, 6 or 8 s
   * at 720p or 1080p, 16:9 or 9:16, with Veo's own sound.
   *
   * Price (Vertex list, Video + Audio, Oct 11 2026): $0.10/s at 720p, $0.12/s
   * at 1080p (sound adds $0.02/s over the silent rates the Director pays).
   * Same yardstick as Seedance above: Solmi sells a credit for ~$0.0137–0.0215
   * (~$0.0175 typical) and the blended AI cost is ~$0.0077 per credit used.
   * Seedance at 12 credits/s costs ~$0.113/s now its promo is over, so
   * ~$0.0094 a credit (~1.9x at $0.0175). Veo at 12/s (720p) is $0.0083 a
   * credit (~2.1x) and at 15/s (1080p) $0.0080 (~2.2x): the same per-second
   * price as Seedance at 720p, a slightly better margin, and 1080p rounded up
   * so it never earns less than 720p. 4 s = 48 credits ($0.40 to us), 8 s at
   * 720p = 96 ($0.80), 8 s at 1080p = 120 ($0.96).
   */
  "veo-fast-t2v": {
    provider: "vertex",
    providerModel: "veo-3.1-fast-generate-001",
    takesImage: false,
    resolution: "720p",
    durations: [4, 6, 8],
    defaultDuration: 8,
    creditsPerSecond: 12,
    creditsPerSecondByResolution: { "720p": 12, "1080p": 15 },
  },
};
/** Seconds the user picked for a direct video engine (its default when unset or invalid). */
export function creativeDirectDuration(
  model: string,
  options: Record<string, string> = {},
) {
  const spec = creativeDirectVideoModels[model];
  if (!spec) return undefined;
  const picked = Number(options.duration);
  return spec.durations.includes(picked) ? picked : spec.defaultDuration;
}
/** Resolution the user picked for a direct video engine (its default when unset or not offered). */
export function creativeDirectResolution(
  model: string,
  options: Record<string, string> = {},
) {
  const spec = creativeDirectVideoModels[model];
  if (!spec) return undefined;
  const picked = options.resolution;
  return picked && spec.creditsPerSecondByResolution?.[picked] !== undefined
    ? picked
    : spec.resolution;
}
/** Credits per second for a direct video engine at the picked resolution. */
export function creativeDirectCreditsPerSecond(
  model: string,
  options: Record<string, string> = {},
) {
  const spec = creativeDirectVideoModels[model];
  if (!spec) return undefined;
  return (
    spec.creditsPerSecondByResolution?.[creativeDirectResolution(model, options)!] ??
    spec.creditsPerSecond
  );
}

/** Feature names are Solmi workflows; model names describe actual integrations. */
export type CreativeField = "image" | "reference" | "mask" | "video";
export type CreativeCategory = "image" | "video" | "audio";
export type CreativeTierId = "fast" | "standard" | "best" | "premium";
/**
 * What each tier is called on the price card. Premium (Oct 11 2026) sits
 * above Best on the AI Video Generator only; rename it here.
 */
export const CREATIVE_TIER_LABELS: Record<CreativeTierId, string> = {
  fast: "Fast",
  standard: "Standard",
  best: "Best",
  premium: "Premium",
};
/** Tier ids in price order: a tool lists its tiers in this order. */
export const CREATIVE_TIER_ORDER: CreativeTierId[] = ["fast", "standard", "best", "premium"];
export type CreativeTier = {
  id: CreativeTierId;
  label: string;
  /** Internal engine behind the tier; never shown on the card. */
  model: string;
  description: string;
};
export type CreativeOption = {
  key: string;
  label: string;
  /** Request body key the choice is sent under. */
  payloadKey: string;
  choices: { value: string; label: string }[];
  default?: string;
  /** The choice is a prompt fragment, not a provider parameter. */
  promptPrefix?: boolean;
};
export type CreativeTool = {
  id: string;
  name: string;
  description: string;
  category: CreativeCategory;
  fields: CreativeField[];
  /** Fields the user may leave empty. */
  optionalFields?: CreativeField[];
  /**
   * The reference field is the person on camera: a preset AI presenter or
   * the user's own photo, sent with the product as reference images.
   */
  presenter?: boolean;
  /** Every engine the server accepts for this tool (tiers plus advanced choices). */
  models: string[];
  /** Quality tiers shown on the card; absent for single-engine utilities and effects. */
  tiers?: CreativeTier[];
  prompt?: string;
  href?: string;
  /** Per-tool wording for upload fields. */
  labels?: Partial<Record<CreativeField, string>>;
  options?: CreativeOption[];
  /** Prompt is optional (a preset or the model supplies it). */
  optionalPrompt?: boolean;
  /**
   * Sent when an optional prompt is left empty, for engines that refuse a
   * request without one.
   */
  defaultPrompt?: string;
  /** Show the client-side adjustment panel before upload (image editor). */
  adjustments?: boolean;
  /**
   * The engines are named presets (video effects): the page is their gallery
   * with a quick-start dialog (CreativeEffectGallery), not an engine picker.
   */
  presetPicker?: boolean;
  /**
   * Shape the aspect-ratio picker starts on, when every tier's engine offers
   * it. Without one, image tools start on 1:1 and video tools on 16:9.
   */
  defaultAspectRatio?: string;
  /**
   * A cheaper Solmi tool on the same engine, offered to visitors whose
   * balance (or a new account's) cannot pay for this one.
   */
  alternative?: { path: string; label: string; text: string };
  /**
   * The result is a cut-out: it is stored as a PNG that keeps its alpha
   * channel instead of being flattened onto white like other image results.
   */
  transparentOutput?: boolean;
};
export const creativeModels: Record<
  string,
  { name: string; credits: number; company: string }
> = {
  ...creativeGatewayModels,
  "z-image": { name: "Z-Image Turbo", credits: 3, company: "Alibaba" },
  seedream: { name: "Seedream 5.0 Lite", credits: 9, company: "ByteDance" },
  inpaint: { name: "Z-Image Inpaint", credits: 5, company: "Alibaba" },
  "image-upscale": { name: "Image Upscaler", credits: 5, company: "WaveSpeed" },
  "image-face-swap": {
    name: "Image Face Swap",
    credits: 12,
    company: "WaveSpeed",
  },
  "background-remove": {
    name: "Background Remover",
    credits: 5,
    company: "WaveSpeed",
  },
  // `credits` is the per-second rate (at the default resolution); see
  // creativeDirectVideoModels.
  "seedance-fast-t2v": { name: "Seedance 2.0 Fast", credits: 12, company: "ByteDance" },
  "seedance-fast-i2v": { name: "Seedance 2.0 Fast", credits: 12, company: "ByteDance" },
  "veo-fast-t2v": { name: "Veo 3.1 Fast", credits: 12, company: "Google" },
  "lucy-edit": { name: "Lucy Edit", credits: 16, company: "Decart" },
  "video-upscale": { name: "Video Upscaler", credits: 3, company: "WaveSpeed" },
  "video-face-swap": {
    name: "Video Face Swap",
    credits: 5,
    company: "WaveSpeed",
  },
};
const legacyCreativeTools: CreativeTool[] = [
  {
    id: "ai-image-generator",
    name: "AI Image Generator",
    description: "Create artwork, covers, and stills from a prompt.",
    category: "image",
    fields: [],
    models: ["seedream", "z-image", ...mobbiGenerationModels],
  },
  {
    id: "relight",
    name: "Relight",
    description: "Change the lighting, color, and atmosphere of a photo.",
    category: "image",
    fields: ["image"],
    models: ["seedream"],
    prompt:
      "Relight this image. Preserve the subject and composition. Lighting direction: ",
  },
  {
    id: "relight-lighting",
    name: "Relight Lighting",
    description: "Direct the light position and color with a prompt.",
    category: "image",
    fields: ["image"],
    models: ["seedream"],
    prompt: "Adjust only the lighting position and color in this image: ",
  },
  {
    id: "inpaint",
    name: "Inpaint",
    description: "Paint an area and describe what should replace it.",
    category: "image",
    fields: ["image", "mask"],
    models: ["inpaint"],
  },
  {
    id: "moodboard",
    name: "Moodboard",
    description: "Combine two visual references into a cohesive moodboard.",
    category: "image",
    fields: ["image", "reference"],
    models: ["seedream"],
    prompt:
      "Create a cohesive editorial moodboard using both reference images. Direction: ",
  },
  {
    id: "character-creator",
    name: "Character Creator",
    description: "Design a character with a consistent reference sheet.",
    category: "image",
    fields: [],
    models: ["seedream"],
    prompt:
      "Create a consistent character reference sheet with front, side, and full-body views: ",
  },
  {
    id: "image-character-swap",
    name: "Character Swap",
    description: "Replace the subject using a character reference.",
    category: "image",
    fields: ["image", "reference"],
    models: ["seedream"],
    prompt:
      "Replace the character in the first image with the character in the second. Preserve the first image's composition and environment. ",
  },
  {
    id: "image-upscaler",
    name: "Image Upscaler",
    description: "Enhance an image to 4K resolution.",
    category: "image",
    fields: ["image"],
    models: ["image-upscale"],
  },
  {
    id: "face-swap",
    name: "Face Swap",
    description: "Transfer a reference face into your photo.",
    category: "image",
    fields: ["image", "reference"],
    models: ["image-face-swap"],
  },
  {
    id: "draw-to-edit",
    name: "Draw to Edit",
    description: "Upload a sketch and turn it into a finished image.",
    category: "image",
    fields: ["image"],
    models: ["seedream"],
    prompt:
      "Turn this sketch into a finished image, following its composition. ",
  },
  {
    id: "fashion-factory",
    name: "Fashion Factory",
    description: "Create a fashion set from a model and outfit reference.",
    category: "image",
    fields: ["image", "reference"],
    models: ["seedream"],
    prompt:
      "Create an editorial fashion set. Dress the model in image one using the outfit in image two. Preserve the model's identity. ",
  },
  {
    id: "image-enhancer",
    name: "Image Enhancer",
    description: "Sharpen, denoise, and enlarge a photo to 4K.",
    category: "image",
    fields: ["image"],
    models: ["image-upscale"],
  },
  {
    id: "hairstyle-changer",
    name: "AI Hairstyle Changer",
    description: "Try a new haircut or color on your photo.",
    category: "image",
    fields: ["image"],
    models: photoPresetModels,
    prompt:
      "Change only the hairstyle of the person in this photo. Keep the face, skin, clothing, background, and lighting exactly the same. New hairstyle: ",
  },
  {
    id: "lyrics-video",
    name: "Lyrics Video",
    description: "Turn a song into a synced lyric video.",
    category: "video",
    fields: [],
    models: [],
    href: "/tools/lyric-video-generator",
  },
  {
    id: "lip-sync-video",
    name: "Lip Sync Video",
    description: "Sync a portrait to your song or audio.",
    category: "video",
    fields: [],
    models: [],
    href: "/ai-singing-video-generator",
  },
  {
    id: "mixed-media",
    name: "Mixed Media",
    description: "Build a music video with references and a storyboard.",
    category: "video",
    fields: [],
    models: [],
    href: "/ai-music-video-generator",
  },
  {
    id: "modify-videos",
    name: "Modify Videos",
    description: "Edit a short clip using natural-language instructions.",
    category: "video",
    fields: ["video"],
    models: ["lucy-edit"],
  },
  {
    id: "lip-sync",
    name: "Lip Sync",
    description: "Create a talking or singing portrait from audio.",
    category: "video",
    fields: [],
    models: [],
    href: "/ai-singing-video-generator",
  },
  {
    id: "video-upscaler",
    name: "Video Upscaler",
    description: "Enhance a short video to 1080p.",
    category: "video",
    fields: ["video"],
    models: ["video-upscale"],
  },
  {
    id: "video-character-swap",
    name: "Character Face Swap",
    description: "Replace a face in a clip using a portrait reference.",
    category: "video",
    fields: ["video", "reference"],
    models: ["video-face-swap"],
  },
  {
    id: "marketing-studio",
    name: "Marketing Studio",
    description: "Turn a product photo into a UGC, demo, or cinematic ad.",
    category: "video",
    fields: ["image"],
    models: ["rf-i2v-grok-imagine-video", "rf-i2v-seedance-2.0-mini", "rf-i2v-kling-v2-6"],
  },
];
const LANGUAGE_CHOICES = [
  ["en", "English"],
  ["zh", "Chinese"],
  ["fr", "French"],
  ["hi", "Hindi"],
  ["it", "Italian"],
  ["ja", "Japanese"],
  ["ko", "Korean"],
  ["pt", "Portuguese"],
  ["ru", "Russian"],
  ["tr", "Turkish"],
  ["es", "Spanish"],
  ["de", "German"],
  ["ar", "Arabic"],
  ["pl", "Polish"],
  ["id", "Indonesian"],
  ["fil", "Filipino"],
].map(([value, label]) => ({ value, label }));
type CreativeAdStyle = {
  value: string;
  label: string;
  prompt: string;
  /** One line that leads a written direction instead of `prompt`. */
  directive: string;
  /** No one on camera, so a chosen presenter is not sent. */
  noPresenter?: boolean;
};
/**
 * Mobbi's Marketing Studio ad styles, as prompt prefixes for image-to-video.
 * `prompt` leads an ad the user gave no direction for; when they wrote one
 * (or a template filled it in), the short `directive` leads instead, so the
 * style's stock scene ("a bedroom or living room") never contradicts the
 * scene the direction describes — Mobbi's generate-ad-video does the same.
 */
export const creativeAdStyles: CreativeAdStyle[] =
  [
    {
      value: "ugc",
      label: "UGC ad",
      prompt:
        "Create a UGC-style product advertisement. A person casually presents the product in the image to the camera, talking enthusiastically about its benefits. Natural, authentic setting like a bedroom or living room, handheld camera feel, natural lighting. They hold up the product, show it from different angles, and share why they love it. ",
      directive:
        "Style: UGC ad — casual, authentic, handheld phone camera, natural lighting, real setting.",
    },
    {
      value: "how-to-demo",
      label: "How-to demo",
      prompt:
        "Create a tutorial-style video for the product in the image. A person demonstrates step-by-step how to use it with clear, well-lit close-up shots, briefly explaining each step. Educational and helpful tone. ",
      directive:
        "Style: How-to demo — step-by-step tutorial, clear close-ups, educational tone.",
    },
    {
      value: "unboxing",
      label: "Unboxing",
      prompt:
        "Create an unboxing video for the product in the image. A person excitedly opens a package revealing the product: close-ups of the packaging opening, tissue paper pulled aside, the product lifted out, genuine excitement. Natural lighting, overhead and eye-level angles. ",
      directive:
        "Style: Unboxing video — satisfying reveal, close-up packaging shots, genuine excitement.",
    },
    {
      value: "product-highlight",
      label: "Product highlight",
      prompt:
        "Create a product highlight video for the product in the image. Dynamic, fast-paced motion graphics from dramatic angles: smooth camera moves, the product spinning and floating, particles and light effects. Energetic and eye-catching, no person, premium 3D-style visualization. ",
      directive:
        "Style: Product highlight — dynamic motion graphics, dramatic angles, premium 3D visualization, no people.",
      noPresenter: true,
    },
    {
      value: "product-review",
      label: "Product review",
      prompt:
        "Create a product review video for the product in the image. A person genuinely reacts to trying it for the first time, examines it closely, tests it, and shares honest impressions. Close-up reaction shots, natural expressions, authentic customer-review tone. ",
      directive:
        "Style: Product review — authentic first impressions, close-up reactions, honest and relatable.",
    },
    {
      value: "cinematic-ad",
      label: "Cinematic ad",
      prompt:
        "Create a cinematic TV commercial for the product in the image. Professional lighting, dramatic camera angles, slow-motion shots, high production value with smooth transitions, music building to a crescendo, ending on a product hero shot. ",
      directive:
        "Style: Cinematic ad — professional lighting, dramatic angles, slow motion, high production value.",
    },
    {
      value: "ugc-virtual-try-on",
      label: "UGC try-on",
      prompt:
        "Create a UGC-style virtual try-on video for the product in the image. A person casually tries on or applies it in a selfie-style video with mirror-style framing and natural lighting, admires the result, and shows it off with a smile. ",
      directive:
        "Style: UGC try-on — selfie-style, the product worn or applied by the person, mirror framing, casual energy.",
    },
    {
      value: "pro-virtual-try-on",
      label: "Professional try-on",
      prompt:
        "Create a professional virtual try-on video for the product in the image. Studio lighting, multiple angles showing the product on a model, clean background, smooth transitions between poses. Polished editorial fashion feel. ",
      directive:
        "Style: Professional try-on — studio lighting, multiple angles, editorial fashion photography.",
    },
    // Higgsfield Marketing Studio's UGC styles (Oct 11 2026); no template
    // renders yet, so the picker shows an icon for them.
    {
      value: "before-after",
      label: "Before/after",
      prompt:
        "Create a before-and-after UGC video ad for the product in the image. It opens on the everyday problem the product solves, then a quick cut to the after: the same person and place, now with the product in use, the difference clear at a glance. Handheld phone camera, natural lighting, a real home setting. It ends with the person holding the product up next to the result. ",
      directive:
        "Style: Before/after — the problem first, a quick cut, then the result with the product, the difference clear at a glance, handheld phone camera.",
    },
    {
      value: "delivery",
      label: "Delivery",
      prompt:
        "Create a delivery-style UGC video ad for the product in the image. A person opens the front door, picks up a parcel from the doorstep, carries it inside, opens it on a table and lifts the product out with real excitement, then shows it to the camera. Handheld phone camera, natural daylight, a casual home setting. ",
      directive:
        "Style: Delivery — the parcel arrives at the door and is opened at home, the product lifted out with real excitement, handheld phone camera.",
    },
  ];
/** Whether an ad style puts a person on camera (so a chosen presenter is used). */
export const creativeAdStyleHasPresenter = (style: string | undefined) =>
  !creativeAdStyles.find((s) => s.value === style)?.noPresenter;
const voiceChoices = (provider: "elevenlabs" | "minimax") =>
  creativeVoices
    .filter((v) => v.provider === provider)
    .map((v) => ({
      value: v.voiceId,
      label: `${v.name} · ${v.style}`,
    }));
export const creativeVoiceOptions: Record<string, CreativeOption> = {
  elevenlabs: {
    key: "voice",
    label: "Voice",
    payloadKey: "voice_id",
    choices: voiceChoices("elevenlabs"),
    default: "JBFqnCBsd6RMkjVDRZzb",
  },
  minimax: {
    key: "voice",
    label: "Voice",
    payloadKey: "voice_id",
    choices: voiceChoices("minimax"),
    default: "Wise_Woman",
  },
};
const tiers = (
  ...entries: [CreativeTierId, string, string][]
): CreativeTier[] =>
  entries
    .filter(([, model]) => creativeModels[model])
    .map(([id, model, description]) => ({
      id,
      model,
      description,
      label: CREATIVE_TIER_LABELS[id],
    }));
const imageTiers = tiers(
  ["fast", "z-image", "Quick drafts and thumbnails"],
  ["standard", "seedream", "Cover art and stills, 2K"],
  ["best", "rf-image-nano-banana-pro", "Sharpest detail and text, 4K-ready"],
);
const editTiers = tiers(
  ["standard", "seedream", "Faithful edits, keeps the subject"],
  ["best", "rf-edit-nano-banana-pro", "Best instruction following"],
);
// Sep 21 2026: Standard was Kling 2.6 and Best was Veo 3.1 Fast. In the first
// 16 h live every Kling job (7/7) and the one Veo job were rejected by the
// provider within ~11 s ("Generation failed", refunded), while PixVerse V6
// finished 21 of 23 — so two of three tiers on the two main video tools were
// dead buttons, and Standard is the default. Luv: Standard = PixVerse V6,
// Best = Seedance 2.0 Fast, no Fast tier. Put Kling/Veo back only after a
// live call has actually succeeded.
// Oct 11 2026: that call succeeded for Veo 3.1 Fast on Vertex AI, through the
// creative tools' own build/submit/poll path (scripts/creative-veo/smoke.mts,
// run by Kitty: 4 s at 720p in 62 s, 1280x720 h264 with AAC sound). Veo is
// the Premium tier of text to video only, above Best: its 8-second default
// is 96 credits. Standard stays the default. Kling stays out.
const videoTiers = tiers(
  ["standard", "rf-t2v-pixverse-v6", "Quick 5-second clips"],
  ["best", "seedance-fast-t2v", "Cinematic motion, follows the prompt closely"],
  ["premium", "veo-fast-t2v", "Sound and up to 1080p; slower, 1–2 min a clip"],
);
const imageVideoTiers = tiers(
  ["standard", "rf-i2v-pixverse-v6", "Quick 5-second clips"],
  ["best", "seedance-fast-i2v", "Natural motion, keeps the subject"],
);
const videoEditTiers = tiers(
  ["fast", "rf-edit-grok-imagine-video-edit", "Quick style and object edits"],
  ["standard", "rf-edit-happyhorse-1.0-video-edit", "Balanced quality, up to 15s"],
  ["best", "rf-edit-wan-2.7-videoedit", "Highest fidelity, up to 10s"],
);
const promoTiers = tiers(
  ["standard", "rf-i2v-grok-imagine-video", "Fast social ads"],
  ["best", "rf-i2v-seedance-2.0-mini", "Follows the brief closely"],
);
/** Public catalog: the workflows a musician needs around a music video. Legacy presets remain only for existing jobs. */
export const creativeTools: CreativeTool[] = [
  {
    id: "ai-image-generator",
    name: "AI Image Generator",
    description: "Cover art, characters, and stills from a prompt.",
    category: "image",
    fields: [],
    models: ["seedream", "z-image", ...mobbiGenerationModels],
    tiers: imageTiers,
  },
  {
    id: "image-to-image",
    name: "Image to Image",
    description: "Transform a photo or artwork with a prompt.",
    category: "image",
    fields: ["image"],
    models: ["seedream", ...mobbiEditModels],
    tiers: editTiers,
  },
  {
    id: "image-editor",
    name: "Image Editor",
    description:
      "Adjust light and color, crop or rotate, then describe an AI edit.",
    category: "image",
    fields: ["image"],
    models: ["seedream", ...mobbiImageEditorModels],
    tiers: editTiers,
    adjustments: true,
    optionalPrompt: true,
    prompt: "Edit this image. ",
  },
  ...legacyCreativeTools.filter((t) => t.id === "inpaint"),
  {
    id: "image-eraser",
    name: "Image Eraser",
    description: "Paint over an object and remove it cleanly.",
    category: "image",
    fields: ["image", "mask"],
    models: ["inpaint"],
    optionalPrompt: true,
    prompt:
      "Remove everything in the masked area and fill it with matching background, seamlessly. ",
  },
  {
    id: "background-remover",
    name: "Background Remover",
    description: "Cut out the subject with a transparent background.",
    category: "image",
    fields: ["image"],
    models: ["background-remove"],
    transparentOutput: true,
  },
  ...legacyCreativeTools.filter((t) => t.id === "image-upscaler"),
  {
    id: "photo-restoration",
    name: "Photo Restoration",
    description: "Repair scratches, fading, and blur in an old photo.",
    category: "image",
    fields: ["image"],
    models: photoPresetModels,
    tiers: editTiers,
    optionalPrompt: true,
    prompt:
      "Restore this old photograph: remove scratches, dust, creases, and fading, recover natural colors and sharp detail, keep every face and feature faithful to the original. ",
  },
  {
    id: "photo-to-cartoon",
    name: "Photo to Cartoon",
    description: "Turn a photo into a cartoon or anime avatar.",
    category: "image",
    fields: ["image"],
    models: photoPresetModels,
    tiers: editTiers,
    optionalPrompt: true,
    options: [
      {
        key: "style",
        label: "Cartoon style",
        payloadKey: "style",
        promptPrefix: true,
        choices: [
          { value: "Pixar-style 3D cartoon", label: "3D cartoon" },
          { value: "Japanese anime", label: "Anime" },
          { value: "Studio Ghibli style", label: "Ghibli" },
          { value: "classic Disney 2D animation", label: "Disney 2D" },
          { value: "comic book ink and halftone", label: "Comic" },
          { value: "pixel art", label: "Pixel art" },
        ],
        default: "Pixar-style 3D cartoon",
      },
    ],
    prompt:
      "Redraw this photo as a {style} illustration. Keep the same person, pose, expression, and composition. ",
  },
  ...legacyCreativeTools.filter((t) => t.id === "face-swap"),
  {
    id: "ai-video-generator",
    name: "AI Video Generator",
    description: "A cinematic clip from text for B-roll and teasers.",
    category: "video",
    fields: [],
    models: ["seedance-fast-t2v", "veo-fast-t2v", ...mobbiTextVideoModels],
    tiers: videoTiers,
  },
  {
    // Sold as the AI video ad generator (Oct 1 2026): any product, with
    // musicians promoting a release or merch as one use case. The id stays
    // "song-promo-ad" (jobs, API allowlist, pricing); the page is the root
    // URL /ai-video-ad-generator (creativeToolRootPaths), slug
    // ai-video-ad-generator (creativeToolSlugs).
    id: "song-promo-ad",
    name: "AI Video Ad Generator",
    description:
      "Turn a product photo, merch, or cover art into a UGC, demo, or cinematic video ad.",
    category: "video",
    fields: ["image", "reference"],
    optionalFields: ["reference"],
    presenter: true,
    models: promoTiers.map((t) => t.model),
    tiers: promoTiers,
    labels: { image: "Product photo, merch, or cover art", reference: "Presenter" },
    // Ads run on TikTok, Reels and Shorts. Both engines offer 9:16; the
    // Standard engine's backup provider takes no shape and follows the photo.
    defaultAspectRatio: "9:16",
    optionalPrompt: true,
    options: [
      {
        key: "adStyle",
        label: "Ad style",
        payloadKey: "adStyle",
        promptPrefix: true,
        choices: creativeAdStyles.map(({ value, label }) => ({ value, label })),
        default: "ugc",
      },
    ],
    prompt: "{adStyle}",
  },
  {
    id: "image-to-video",
    name: "Image to Video",
    description: "Animate a photo, illustration, or sketch into a video.",
    category: "video",
    fields: ["image"],
    models: ["seedance-fast-i2v", ...mobbiImageVideoModels],
    tiers: imageVideoTiers,
  },
  {
    id: "transition-video",
    name: "Transition",
    description: "Morph from a start frame to an end frame.",
    category: "video",
    fields: ["image", "reference"],
    models: mobbiTransitionModels,
    tiers: tiers(
      ["standard", "rf-tr-pixverse-v6-transition", "Smooth 5-second morph"],
      ["best", "rf-tr-pixverse-c1-transition", "Higher fidelity morph"],
    ),
    labels: { image: "Start frame", reference: "End frame" },
    optionalPrompt: true,
    // PixVerse (direct and on WaveSpeed) refuses a transition with no prompt
    // (Oct 2 2026 smoke test: "Required field prompt are missing or empty").
    defaultPrompt:
      "A smooth, natural transition from the start frame to the end frame.",
  },
  {
    id: "reference-to-video",
    name: "Reference to Video",
    description:
      "Keep your character or artwork consistent using reference images.",
    category: "video",
    fields: ["image", "reference"],
    optionalFields: ["reference"],
    // Best was Kling O1 reference-to-video; every Kling route failed live
    // (see videoTiers). The provider has no Seedance 2.0 Fast *reference*
    // model, only t2v / i2v, so Best runs the i2v engine led by reference
    // image 1 — reference image 2 only applies on Standard (PixVerse C1).
    models: [...mobbiReferenceVideoModels, "seedance-fast-i2v"],
    tiers: tiers(
      ["standard", "rf-r2v-pixverse-c1-r2v", "Up to 2 references"],
      ["best", "seedance-fast-i2v", "Uses first reference"],
    ),
    labels: {
      image: "Reference image 1",
      reference: "Reference image 2 (optional)",
    },
  },
  {
    id: "video-effects",
    name: "Video Effects",
    description:
      "One-tap effects for performers: dances, morphs, style looks, and stunts.",
    category: "video",
    fields: ["image"],
    // PixVerse effect templates; the page is their gallery (CreativeEffectGallery).
    models: creativeEffects.map((effect) => effect.id),
    presetPicker: true,
  },
  {
    id: "video-extender",
    name: "Video Extender",
    description: "Continue a clip for a few more seconds.",
    category: "video",
    fields: ["video"],
    models: mobbiVideoExtendModels,
    tiers: tiers(
      ["fast", "rf-ext-pixverse-v6-extend", "Quick continuation"],
      ["standard", "rf-ext-seedance-2.0-mini-video-extend", "Keeps motion and sound"],
      ["best", "rf-ext-seedance-2.0-video-extend", "Highest fidelity"],
    ),
    optionalPrompt: true,
    // Every tier's WaveSpeed route requires a prompt; without one the job
    // fails whenever Renderful is unavailable (Oct 2 2026 smoke test).
    defaultPrompt:
      "Continue the video naturally, keeping the same subject, motion, lighting, and camera.",
  },
  ...legacyCreativeTools
    .filter((t) => t.id === "modify-videos")
    .map((t) => ({
      ...t,
      name: "Video Editor",
      models: mobbiVideoEditModels,
      tiers: videoEditTiers,
    })),
  {
    id: "video-background-remover",
    name: "Video Background Remover",
    description: "Replace or clear the background behind the subject.",
    category: "video",
    fields: ["video"],
    models: mobbiVideoEditModels.filter((id) => id !== "rf-edit-gen4-aleph"),
    tiers: videoEditTiers,
    optionalPrompt: true,
    prompt:
      "Remove the background of this video and keep the main subject exactly as is. New background: ",
  },
  {
    id: "video-eraser",
    name: "Video Eraser",
    description: "Describe an object or person to remove from a clip.",
    category: "video",
    fields: ["video"],
    models: mobbiVideoEditModels.filter((id) => id !== "rf-edit-gen4-aleph"),
    tiers: videoEditTiers,
    prompt:
      "Remove the following from every frame of this video and fill the space naturally, keeping everything else unchanged: ",
  },
  {
    id: "mimic",
    name: "Motion Transfer",
    description: "Make a photo copy the moves from a reference video.",
    category: "video",
    fields: ["image", "video"],
    // Single engine, no tier picker (Luv, Sep 21 2026): Wan 2.2 Animate.
    // Kling 2.6 Motion Control was the Standard tier and is dropped with the
    // other Kling routes.
    models: ["rf-mimic-wan-2.2-animate"],
    labels: { image: "Character photo", video: "Motion video (2–15 seconds)" },
    optionalPrompt: true,
    // 186 credits is out of a new account's reach: none of the 17 sign-ups
    // on this page from Sep 28 to Oct 8 2026 ran it. Make Photo Dance runs
    // the same engine (Wan 2.2 Animate) from 5 credits a second
    // (DANCE_VIDEO_RATES) on music-video credits, which new accounts get.
    alternative: {
      path: "/make-photo-dance",
      label: "Try Make Photo Dance",
      text: "Make Photo Dance runs the same motion engine from 5 credits a second.",
    },
    options: [
      {
        key: "orientation",
        label: "Follow framing of",
        payloadKey: "character_orientation",
        choices: [
          { value: "video", label: "The motion video" },
          { value: "image", label: "The character photo" },
        ],
        default: "video",
      },
    ],
  },
  ...legacyCreativeTools.filter((t) => t.id === "video-upscaler"),
  ...legacyCreativeTools.filter((t) => t.id === "lip-sync"),
  ...legacyCreativeTools
    .filter((t) => t.id === "video-character-swap")
    .map((t) => ({ ...t, name: "Video Face Swap" })),
  {
    id: "voiceover",
    name: "AI Voiceover",
    description: "Spoken intros, ad reads, and narration in 70+ languages.",
    category: "audio",
    fields: [],
    models: mobbiSpeechModels,
    tiers: tiers(
      ["fast", "rf-tts-eleven_flash_v2_5", "Clear and quick"],
      ["standard", "rf-tts-speech-02-hd", "Warm, natural delivery"],
      ["best", "rf-tts-eleven_v3", "Most expressive"],
    ),
  },
  {
    id: "video-translate",
    name: "Video Translate",
    description: "Dub a clip into another language, keeping the voice.",
    category: "audio",
    fields: ["video"],
    models: mobbiDubbingModels,
    labels: { video: "Video or audio clip (up to 3 minutes)" },
    optionalPrompt: true,
    options: [
      {
        key: "targetLang",
        label: "Translate to",
        payloadKey: "target_lang",
        choices: LANGUAGE_CHOICES,
        default: "es",
      },
    ],
  },
];
export const findCreativeToolForJob = (id: string) =>
  creativeTools.find((t) => t.id === id) ||
  legacyCreativeTools.find((t) => t.id === id) ||
  // A finished ad (shared/creativeAdFinish.ts) lists and plays like a result.
  (id === adFinishTool.id ? adFinishTool : undefined);
/**
 * Public URL slug per tool id, where the slug differs from the id. The id is
 * the contract with the jobs API, stored jobs, pricing and the server
 * allowlist, so it never changes; the slug is only what appears in the URL and
 * is worded for search ("ai video editor", "motion transfer", "ai voiceover",
 * "ai video ad generator"). The old id-based paths 301 to these
 * (PERMANENT_REDIRECTS in seo.ts).
 */
export const creativeToolSlugs: Record<string, string> = {
  "modify-videos": "ai-video-editor",
  mimic: "motion-transfer",
  voiceover: "ai-voiceover",
  "song-promo-ad": "ai-video-ad-generator",
};
const creativeToolIdsBySlug: Record<string, string> = Object.fromEntries(
  Object.entries(creativeToolSlugs).map(([id, slug]) => [slug, id]),
);
export const creativeToolSlug = (toolId: string) =>
  creativeToolSlugs[toolId] ?? toolId;
/** Tool id for a URL segment. Accepts the slug and, for old links, the id. */
export const creativeToolIdFromSlug = (segment: string) =>
  creativeToolIdsBySlug[segment] ?? segment;
/**
 * Tools whose page lives at the site root instead of /creative-tools/<slug>.
 * The AI video ad generator is sold to advertisers as a standalone product
 * (Oct 2 2026), so /ai-video-ad-generator is its canonical URL and
 * /creative-tools/ai-video-ad-generator (and the old id path) 301 here.
 */
export const creativeToolRootPaths: Record<string, string> = {
  "song-promo-ad": "/ai-video-ad-generator",
};
/** The canonical page path for a tool id (root path or /creative-tools/<slug>). */
export const creativeToolPagePath = (toolId: string) =>
  creativeToolRootPaths[toolId] ?? `/creative-tools/${creativeToolSlug(toolId)}`;
export const creativeToolPath = (tool: CreativeTool) =>
  tool.href || creativeToolPagePath(tool.id);
/**
 * Preset landings: a base tool pre-configured for one musician job (a Spotify
 * Canvas loop, an animated album cover, a character design). Each preset is
 * its own URL under /creative-tools/ with its own SEO copy; the studio
 * underneath is the base tool (image or video) with the aspect ratio,
 * duration and prompt filled in, all editable; duration applies only where
 * the engine offers one.
 * Jobs are created and stored under the base tool id, so the API, server
 * allowlist and pricing are untouched.
 */
export type CreativeToolPreset = {
  toolId: string;
  name: string;
  /** Starting aspect ratio; must be one the base tool's engines accept. */
  aspectRatio?: string;
  /** Starting duration in seconds, applied when the engine offers that choice. */
  duration?: number;
  /** Pre-filled, editable prompt. */
  prompt?: string;
  /** Starting engine: one of the base tool's tier models (model landing pages). */
  model?: string;
  /** Per-preset wording for upload fields (the base tool's labels otherwise). */
  labels?: Partial<Record<CreativeField, string>>;
  /**
   * A model landing page named after the third-party model it opens on
   * (Oct 3 2026, Kitty approved Seedance 2.0 and PixVerse; Oct 11 Veo 3). Everywhere else
   * the vendor rule in docs/creative-tools.md ("Positioning") still holds:
   * no model names on price cards, tier labels or navigation.
   * - `names`: the only vendor words this page's copy may use.
   * - `family`: a substring every claimed tier's model id contains.
   * - `runsOn`: every [tool id, tier id] the copy says runs the model. A test
   *   checks each against the tier config, so swapping an engine fails the
   *   build instead of leaving a false page live.
   */
  modelPage?: {
    names: string[];
    family: string;
    runsOn: [toolId: string, tier: string][];
  };
  /**
   * Served at /<slug> instead of /creative-tools/<slug>, like the tools in
   * creativeToolRootPaths: the ad pages sold to advertisers next to
   * /ai-video-ad-generator. The folder path 301s to the root URL.
   */
  root?: boolean;
  /**
   * Starting values for the base tool's own options (key → choice value),
   * e.g. the ad style. Each must be one of that option's choices; all stay
   * editable.
   */
  options?: Record<string, string>;
  /**
   * Ad generator presets: the template gallery shows only the templates in
   * these ad styles (creativeAdStyles values), in gallery order.
   */
  templateStyles?: string[];
};
export const creativeToolPresets: Record<string, CreativeToolPreset> = {
  // Model landing pages (Oct 3 2026, Ahrefs US: "seedance 2.0" 75k/mo with a
  // DR 36 site at #5; "pixverse ai" 28k with a DR 25 site at #7). Each opens
  // image to video on the tier that really runs the model.
  "seedance-2-0": {
    toolId: "image-to-video",
    name: "Seedance 2.0 Video Generator",
    model: "seedance-fast-i2v",
    prompt:
      "Slow cinematic push-in on the subject as the light shifts, hair and clothes moving gently in the wind, the scene stays true to the photo.",
    modelPage: {
      names: ["seedance", "bytedance"],
      family: "seedance",
      runsOn: [
        ["image-to-video", "best"],
        ["ai-video-generator", "best"],
        ["reference-to-video", "best"],
        ["video-extender", "standard"],
        ["video-extender", "best"],
      ],
    },
  },
  "pixverse-ai": {
    toolId: "image-to-video",
    name: "PixVerse AI Video Generator",
    model: "rf-i2v-pixverse-v6",
    prompt:
      "Smooth camera orbit around the subject, light flickering across the scene, natural motion that keeps the look of the photo.",
    modelPage: {
      names: ["pixverse"],
      family: "pixverse",
      runsOn: [
        ["image-to-video", "standard"],
        ["ai-video-generator", "standard"],
        ["transition-video", "standard"],
        ["transition-video", "best"],
        ["reference-to-video", "standard"],
        ["video-extender", "fast"],
      ],
    },
  },
  // Oct 11 2026: "veo 3" is 7k/mo US but 559k worldwide (IN 96k, PK 31k,
  // BR 28k, DE 24k, EG 22k, MX 18k), KD 0, with third-party model pages on
  // page 1. Opens text to video on the Premium tier, the only one Veo runs.
  "veo-3": {
    toolId: "ai-video-generator",
    name: "Veo 3 AI Video Generator",
    model: "veo-fast-t2v",
    prompt:
      "A street violinist plays under a stone bridge at dusk while rain drips from the arches; the melody echoes off the walls and distant traffic hums. Slow push-in, warm lamplight on wet cobblestones.",
    modelPage: {
      names: ["veo", "google"],
      family: "veo",
      runsOn: [["ai-video-generator", "premium"]],
    },
  },
  "spotify-canvas-maker": {
    toolId: "image-to-video",
    name: "Spotify Canvas Maker",
    aspectRatio: "9:16",
    duration: 8,
    prompt:
      "Slow, subtle motion that loops cleanly: gentle camera drift, soft moving light and particles, the artwork stays centered and fully recognizable.",
  },
  "animated-album-cover": {
    toolId: "image-to-video",
    name: "Animated Album Cover Maker",
    aspectRatio: "9:16",
    duration: 5,
    prompt:
      "Bring this album cover to life with subtle, elegant motion: slow parallax, drifting light, hair or fabric moving gently, composition and title unchanged.",
  },
  // Oct 3 2026: "ai character generator" (23k/mo US), "character generator",
  // "oc generator", "character design generator". A text-to-image preset:
  // the sample character leads the prompt so it is the part people rewrite,
  // and the rest asks for a reusable design sheet look (full body, front
  // view, plain background). 9:16 is the one portrait shape every image tier
  // takes (Fast and Standard offer 1:1, 16:9 and 9:16 only), so a full
  // standing figure never falls back to square.
  "ai-character-generator": {
    toolId: "ai-image-generator",
    name: "AI Character Generator",
    aspectRatio: "9:16",
    prompt:
      "Character design of a virtual pop singer with short silver hair, a cropped red jacket, black cargo pants and white sneakers, holding a microphone. Full body, front view, relaxed standing pose, plain light background, clear silhouette, consistent outfit and colors, clean illustration, no text.",
  },
  // Oct 9 2026: photo-edit presets, the openart /features/ and krea
  // /apps/edit/ pattern (seo/competitor-seo-patterns-2026-10-08.md §1). Each
  // opens Image to Image with the instruction written out: the part people
  // rewrite (the haircut, the shade, the drawing medium, the look) leads, and
  // the rest tells the engine what to keep. Image to Image sends the prompt
  // as typed (no prefix of its own), needs one, and runs the same Standard and
  // Best edit tiers as photo restoration and photo to cartoon. Ahrefs US:
  // "ai hairstyle changer" 10k KD 41, "ai hairstyle" 5.3k KD 31, "hairstyle
  // try on" 1.9k KD 0, "haircut filter" 1.8k KD 10, "what would i look like
  // with a buzz cut" 1.1k KD 4, "... bald" 1k KD 0, "... short hair" 800 KD 0.
  // The retired hairstyle-changer id now 301s here (creativeToolsSeoPaths).
  "ai-hairstyle-changer": {
    toolId: "image-to-image",
    name: "AI Hairstyle Changer",
    labels: { image: "Your photo (face and hairline clearly visible)" },
    prompt:
      "Give the person in this photo a buzz cut. Change only the hair: keep the face, skin, expression, clothing, background, and lighting exactly the same. Realistic hair with a natural hairline.",
  },
  // "ai hair color changer" 3.9k KD 14, "hair color try on" 6.5k KD 38,
  // "change hair color in photo" 700 KD 1, "hair color ai" 700 KD 0.
  "ai-hair-color-changer": {
    toolId: "image-to-image",
    name: "AI Hair Color Changer",
    labels: { image: "Your photo (hair clearly visible)" },
    prompt:
      "Change the hair color of the person in this photo to platinum blonde. Keep the haircut, hair length, face, skin, clothing, background, and lighting exactly the same. Natural roots, shine, and strand detail.",
  },
  // "turn photo into sketch" 700 KD 0 (pollo #1 with no referring domains),
  // "ai sketch" 600 KD 0, "photo to line drawing" 450 KD 22; the head
  // "photo to sketch" is 1.9k at KD 51.
  "photo-to-sketch": {
    toolId: "image-to-image",
    name: "Photo to Sketch",
    labels: { image: "Photo to draw" },
    prompt:
      "Redraw this photo as a graphite pencil sketch on white paper: clean outlines, soft cross-hatched shading, no color. Keep the same people, pose, expression, and composition.",
  },
  // Music-lane theme pages (§3). "ai profile picture generator" 2k KD 26 and
  // "pfp generator" 900 KD 0; the SERP (Canva, pfpmaker, Higgsfield, Adobe)
  // starts from the visitor's own photo, so this one is a photo edit too.
  // Square: profile pictures are shown in square or round slots.
  "ai-profile-picture-generator": {
    toolId: "image-to-image",
    name: "AI Profile Picture Generator",
    aspectRatio: "1:1",
    labels: { image: "Your photo" },
    prompt:
      "Studio press portrait with soft key light and a deep purple backdrop. Turn this photo into a profile picture of the same person: head and shoulders, centered, looking into the camera, sharp focus. Keep the face, hair, and skin tone true to the original. No text.",
  },
  // "sticker designer" 1.6k KD 3, "band stickers" 1.2k KD 1, "ai sticker
  // maker" 800 KD 33. Text to image on the AI image generator. The gray
  // backdrop keeps the white border visible: creative image results are
  // stored flattened (ingestAiVideoImageUrl), so no output is transparent.
  "ai-sticker-maker": {
    toolId: "ai-image-generator",
    name: "AI Sticker Maker",
    aspectRatio: "1:1",
    prompt:
      "Die-cut sticker of a cartoon cassette tape with lightning bolts, for a band. Thick white border around the shape, bold clean outlines, flat bright colors, centered on a plain gray background, no shadow, no mockup.",
  },
  // "ai shirt design" 900 KD 30 (gptshirt.ai DR 2 at #3, sizzlepop DR 7 at
  // #9), "ai t shirt design" 400 KD 30. The artwork, not a shirt mockup.
  "ai-t-shirt-design": {
    toolId: "ai-image-generator",
    name: "AI Shirt Design Generator",
    aspectRatio: "1:1",
    prompt:
      'T-shirt graphic for a band called "Velvet Static": a skull wearing headphones, framed by roses, with the band name in bold vintage lettering above it. Screen-print style, three flat colors, bold clean lines, centered on a plain light gray background. Design only, no shirt, no mockup.',
  },
  // Oct 11 2026: sibling pages of the AI video ad generator, one search
  // intent each (the Zeely / Topview pattern). Each is the same studio
  // (song-promo-ad) opened on the ad style the query asks for, with the
  // template gallery cut to the matching styles; nothing else changes, so
  // prices, tiers and jobs are the ad generator's. Root URLs like the ad
  // generator itself. Ahrefs US (Oct 1–9 pulls; Ahrefs returned 401 on Oct
  // 11): "ai ugc video generator" 600 KD 36; "ai product video generator" 700
  // KD 40 (traffic potential 94k), "product video maker" 200 KD 12; "ai
  // commercial generator" 900 (KD 0 on Oct 9, 39 on Oct 1), "commercial
  // maker" 700 KD 12. The commercial terms moved here from
  // /ai-video-ad-generator's title.
  "ai-ugc-video-generator": {
    toolId: "song-promo-ad",
    name: "AI UGC Video Generator",
    root: true,
    options: { adStyle: "ugc" },
    templateStyles: ["ugc", "ugc-virtual-try-on", "product-review"],
  },
  // Product highlight puts no one on camera, so the presenter slot hides.
  "ai-product-video-generator": {
    toolId: "song-promo-ad",
    name: "AI Product Video Generator",
    root: true,
    options: { adStyle: "product-highlight" },
    templateStyles: ["product-highlight", "unboxing", "how-to-demo"],
  },
  "ai-commercial-generator": {
    toolId: "song-promo-ad",
    name: "AI Commercial Generator",
    root: true,
    options: { adStyle: "cinematic-ad" },
    templateStyles: ["cinematic-ad", "pro-virtual-try-on", "product-highlight"],
  },
};
/** The page path of a preset: /<slug> for a root preset, else /creative-tools/<slug>. */
export const creativePresetPagePath = (slug: string) =>
  creativeToolPresets[slug]?.root ? `/${slug}` : `/creative-tools/${slug}`;
/** Every creative page served outside /creative-tools/ (root tools and root presets). */
export const creativeRootPagePaths: string[] = [
  ...Object.values(creativeToolRootPaths),
  ...Object.keys(creativeToolPresets)
    .filter((slug) => creativeToolPresets[slug].root)
    .map((slug) => `/${slug}`),
];
/**
 * AI Video Generator is one entry point with three ways in (Luv, Sep 21
 * 2026): from a prompt, from a photo, or from reference images. Each mode is
 * still its own tool id — its own fields, tiers and server validation — so
 * the switch only changes which tool page is showing; the prompt rides along.
 */
export const creativeVideoGeneratorModes: { toolId: string; label: string }[] = [
  { toolId: "ai-video-generator", label: "Text to video" },
  { toolId: "image-to-video", label: "Image to video" },
  { toolId: "reference-to-video", label: "Reference to video" },
];
export const isCreativeVideoGeneratorMode = (toolId: string) =>
  creativeVideoGeneratorModes.some((mode) => mode.toolId === toolId);

/** The engine a tool starts on: its Standard tier, else its first tier, else its first model. */
export const creativeDefaultModel = (tool: CreativeTool) =>
  tool.tiers?.find((t) => t.id === "standard")?.model ||
  tool.tiers?.[0]?.model ||
  tool.models[0];
export const creativeTierFor = (tool: CreativeTool, model: string) =>
  tool.tiers?.find((t) => t.model === model);
export const isPerSecondModel = (model: string) =>
  ["lucy-edit", "video-upscale", "video-face-swap"].includes(model) ||
  Boolean(creativeGatewayModels[model]?.perSecond);
export const isPerMinuteModel = (model: string) =>
  Boolean(creativeGatewayModels[model]?.perMinute);
export const isPerKCharsModel = (model: string) =>
  Boolean(creativeGatewayModels[model]?.perKChars);
export const needsCreativePrompt = (tool: CreativeTool) =>
  !tool.optionalPrompt &&
  ![
    // A PixVerse template ignores the prompt; the effect is the direction.
    "video-effects",
    "image-upscaler",
    "background-remover",
    "face-swap",
    "video-upscaler",
    "video-character-swap",
  ].includes(tool.id);
/** Options a tool exposes for a given model (speech voices depend on the model's provider). */
export function creativeToolOptions(tool: CreativeTool, model: string) {
  const output = creativeOutputSettings[model];
  const provider = creativeGatewayModels[model]?.voiceProvider;
  const direct = creativeDirectVideoModels[model];
  return [
    ...(provider ? [creativeVoiceOptions[provider]] : []),
    ...(output?.resolutions.length ? [{ key: "resolution", label: "Resolution", payloadKey: "resolution", choices: output.resolutions.map(value => ({value, label: value.toUpperCase()})), default: output.defaultResolution } satisfies CreativeOption] : []),
    ...(output?.durations.length ? [{ key: "duration", label: "Duration", payloadKey: "duration", choices: output.durations.map(value => ({value: String(value), label: `${value} ${value === 1 ? "second" : "seconds"}`})), default: String(output.defaultDuration) } satisfies CreativeOption] : []),
    // A direct engine with a per-resolution rate (Veo: 720p or 1080p).
    ...(direct?.creditsPerSecondByResolution
      ? [
          {
            key: "resolution",
            label: "Resolution",
            payloadKey: "resolution",
            choices: Object.keys(direct.creditsPerSecondByResolution).map(
              (value) => ({ value, label: value.toUpperCase() }),
            ),
            default: direct.resolution,
          } satisfies CreativeOption,
        ]
      : []),
    ...(direct
      ? [
          {
            key: "duration",
            label: "Duration",
            payloadKey: "duration",
            choices: direct.durations.map((seconds) => ({
              value: String(seconds),
              label: `${seconds} seconds`,
            })),
            default: String(direct.defaultDuration),
          } satisfies CreativeOption,
        ]
      : []),
    ...(tool.options || []),
  ];
}
/**
 * Resolve the tool's prompt prefix with any prompt-fragment options. An ad
 * style leads with its full scene when the user wrote no direction, and with
 * its one-line directive when they did (see creativeAdStyles).
 */
export function creativePromptPrefix(
  tool: CreativeTool,
  options: Record<string, string> = {},
  direction = "",
) {
  let prefix = tool.prompt || "";
  for (const option of tool.options || []) {
    if (!option.promptPrefix) continue;
    const value = options[option.key] ?? option.default ?? "";
    const style =
      option.key === "adStyle"
        ? creativeAdStyles.find((s) => s.value === value)
        : undefined;
    const fragment =
      option.key === "adStyle"
        ? style
          ? direction.trim()
            ? `${style.directive}\n\n`
            : style.prompt
          : ""
        : value;
    prefix = prefix.replaceAll(`{${option.key}}`, fragment);
  }
  return prefix;
}
/** Credits for one request given the prompt and (for clip inputs) its duration. */
export function creativeCost(
  model: string,
  {
    prompt = "",
    seconds = 1,
    options = {},
  }: { prompt?: string; seconds?: number; options?: Record<string, string> },
) {
  const direct = creativeDirectVideoModels[model];
  if (direct)
    return (
      creativeDirectCreditsPerSecond(model, options)! *
      creativeDirectDuration(model, options)!
    );
  const base = creativeOutputPrice(model, options);
  if (isPerSecondModel(model)) return base * Math.max(1, Math.ceil(seconds));
  if (isPerMinuteModel(model)) return base * Math.max(1, Math.ceil(seconds / 60));
  if (isPerKCharsModel(model))
    return base * Math.max(1, Math.ceil(prompt.length / 1000));
  return base;
}
export const creativeOutputKind = (
  tool: CreativeTool | undefined,
  model: string,
): "image" | "video" | "audio" | "model" =>
  creativeGatewayModels[model]?.output ||
  (tool?.category === "audio" ? "audio" : tool?.category || "image");

/** Header dropdowns: audio rides with Video. */
export const creativeMenuCategories: Record<
  "image" | "video",
  CreativeCategory[]
> = { image: ["image"], video: ["video", "audio"] };
export const creativeCategoryLabels: Record<CreativeCategory, string> = {
  image: "Image",
  video: "Video",
  audio: "Audio",
};

/** Preserve catalog order within each company and only include available choices. */
export function groupCreativeModels(models: readonly string[]) {
  const groups = new Map<string, string[]>();
  for (const model of models) {
    const company = creativeModels[model].company;
    const items = groups.get(company) || [];
    items.push(model);
    groups.set(company, items);
  }
  return Array.from(groups, ([company, models]) => ({ company, models }));
}

export const creativeOutputSettings: Record<string, {resolutions: string[]; durations: number[]; defaultResolution: string; defaultDuration: number; prices: Record<string, number>}> = outputCatalog;
export function creativeOutputPrice(model: string, options: Record<string, string> = {}) {
  const config = creativeOutputSettings[model];
  if (!config) return creativeModels[model].credits;
  const resolution = options.resolution ?? config.defaultResolution;
  const duration = options.duration ?? String(config.defaultDuration);
  return config.prices[`${resolution}:${duration}`] ?? creativeModels[model].credits;
}
